import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { UserCheck, Settings, Clock, Bell, Users, RotateCcw, Calendar, Lock } from 'lucide-react';
import { Form } from '@/components/ui/form';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/ui/use-toast';
import { SettingsCard, SwitchField, SettingsField, EditControls, SectionHeader } from '../ui';
import { 
  ExtendedBusinessDetails, 
  ShiftAssignmentSettings, 
  createShiftDefaults,
  resetShiftSettingsForm 
} from '../utils';

// Schema for shift assignment settings only
const shiftSettingsSchema = z.object({
  allow_shift_self_assignment: z.boolean().default(false),
  shift_request_advance_hours: z.number().min(1).max(168).default(2),
  shift_request_deadline_days: z.number().min(1).max(30).default(14),
  auto_approve_requests: z.boolean().default(false),
  manager_notification_mode: z.enum(["immediate", "hourly", "daily"]).default("immediate"),
  request_expiry_hours: z.number().min(1).max(168).default(48),
  max_pending_requests: z.number().min(1).max(20).default(5),
  max_requests_per_day: z.number().min(1).max(10).default(3),
  require_request_notes: z.boolean().default(false),
  enable_employee_notifications: z.boolean().default(true),
  enable_manager_email_notifications: z.boolean().default(true),
  request_priority_mode: z.enum(["first-come", "seniority", "rotation"]).default("first-come"),
  enable_shift_swapping: z.boolean().default(false),
  require_swap_approval: z.boolean().default(true),
  swap_advance_hours: z.number().min(1).max(168).default(24),
  // Historical edit grace period
  historical_edit_grace_days: z.number().min(0).max(30).default(3),
});

interface ShiftSelfAssignmentSettingsProps {
  businessDetails: ExtendedBusinessDetails;
  onUpdateSuccess: (updatedDetails: Partial<ExtendedBusinessDetails>) => void;
}

export const ShiftSelfAssignmentSettings: React.FC<ShiftSelfAssignmentSettingsProps> = ({
  businessDetails,
  onUpdateSuccess,
}) => {
  const [loading, setLoading] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [shiftSelfAssignmentEnabled, setShiftSelfAssignmentEnabled] = useState(
    businessDetails.allow_shift_self_assignment || false
  );
  const [shiftSwappingEnabled, setShiftSwappingEnabled] = useState(
    businessDetails.enable_shift_swapping || false
  );

  const form = useForm<ShiftAssignmentSettings>({
    resolver: zodResolver(shiftSettingsSchema),
    defaultValues: createShiftDefaults(businessDetails),
  });

  // Effect to reset form when businessDetails prop changes
  useEffect(() => {
    form.reset(createShiftDefaults(businessDetails));
    setShiftSelfAssignmentEnabled(businessDetails.allow_shift_self_assignment || false);
    setShiftSwappingEnabled(businessDetails.enable_shift_swapping || false);
  }, [businessDetails, form]);

  // Watch form values and update local state
  useEffect(() => {
    const subscription = form.watch((values) => {
      if (values.allow_shift_self_assignment !== undefined) {
        setShiftSelfAssignmentEnabled(values.allow_shift_self_assignment);
      }
      if (values.enable_shift_swapping !== undefined) {
        setShiftSwappingEnabled(values.enable_shift_swapping);
      }
    });
    return () => subscription.unsubscribe();
  }, [form]);

  const onSubmit = async (values: ShiftAssignmentSettings) => {
    setLoading(true);
    try {
      const updates = {
        allow_shift_self_assignment: values.allow_shift_self_assignment,
        shift_request_advance_hours: values.shift_request_advance_hours,
        shift_request_deadline_days: values.shift_request_deadline_days,
        auto_approve_requests: values.auto_approve_requests,
        manager_notification_mode: values.manager_notification_mode,
        request_expiry_hours: values.request_expiry_hours,
        max_pending_requests: values.max_pending_requests,
        max_requests_per_day: values.max_requests_per_day,
        require_request_notes: values.require_request_notes,
        enable_employee_notifications: values.enable_employee_notifications,
        enable_manager_email_notifications: values.enable_manager_email_notifications,
        request_priority_mode: values.request_priority_mode,
        enable_shift_swapping: values.enable_shift_swapping,
        require_swap_approval: values.require_swap_approval,
        swap_advance_hours: values.swap_advance_hours,
        historical_edit_grace_days: values.historical_edit_grace_days,
        updated_at: new Date().toISOString(),
      };

      const { error } = await supabase
        .from("businesses")
        .update(updates)
        .eq("id", businessDetails.id);

      if (error) throw error;

      toast.success("Shift assignment settings updated successfully!");
      setIsEditing(false);
      onUpdateSuccess(updates);
    } catch (error: unknown) {
      console.error("Error updating shift settings:", error);
      toast.error(`Failed to update shift settings: ${error instanceof Error ? error.message : String(error)}`);
    } finally {
      setLoading(false);
    }
  };

  const handleStartEditing = () => {
    setIsEditing(true);
  };

  const handleCancelEditing = () => {
    setIsEditing(false);
    resetShiftSettingsForm(form, businessDetails);
    setShiftSelfAssignmentEnabled(businessDetails.allow_shift_self_assignment || false);
    setShiftSwappingEnabled(businessDetails.enable_shift_swapping || false);
  };

  return (
    <SettingsCard 
      icon={UserCheck} 
      title="Employee Shift Self-Assignment" 
      description="Configure employee shift request settings and policies"
    >
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
          <div className="space-y-4">
            <SwitchField
              name="allow_shift_self_assignment"
              label="Enable Self-Assignment"
              description="Allow employees to request assignment to available shifts. Requests require manager approval."
              disabled={!isEditing}
              form={form}
              labelSize="base"
              className="p-4"
            />

            {/* Additional Settings Section - Shown when self-assignment is enabled */}
            {shiftSelfAssignmentEnabled && (
              <div className="space-y-6 pt-6 border-t border-border">
                <div className="flex items-center gap-2 mb-4">
                  <Settings className="h-4 w-4 text-primary" />
                  <h4 className="text-sm font-semibold text-foreground">Self-Assignment Configuration</h4>
                </div>
                
                {/* Request Time Limits Section */}
                <div className="space-y-3">
                  <SectionHeader icon={Clock} title="Request Time Limits" />
                  <div className="grid grid-cols-1 gap-4">
                    <SettingsField
                      type="input"
                      name="shift_request_advance_hours"
                      label="Minimum Advance Notice (hours)"
                      placeholder="2"
                      inputType="number"
                      min="1"
                      max="168"
                      disabled={!isEditing}
                      form={form}
                      description="How far in advance employees must request shifts (1-168 hours)"
                    />
                    
                    <SettingsField
                      type="input"
                      name="shift_request_deadline_days"
                      label="Request Deadline (days)"
                      placeholder="14"
                      inputType="number"
                      min="1"
                      max="30"
                      disabled={!isEditing}
                      form={form}
                      description="How far in advance employees can request shifts (1-30 days)"
                    />
                  </div>
                </div>

                {/* Approval & Notification Settings */}
                <div className="space-y-3">
                  <SectionHeader icon={Bell} title="Approval & Notifications" />
                  
                  <div className="grid grid-cols-1 gap-4">
                    <SwitchField
                      name="auto_approve_requests"
                      label="Auto-approve Requests"
                      description="Automatically approve requests under certain conditions"
                      disabled={!isEditing}
                      form={form}
                    />

                    <SettingsField
                      type="select"
                      name="manager_notification_mode"
                      label="Manager Notifications"
                      placeholder="Select notification frequency"
                      disabled={!isEditing}
                      form={form}
                      description="How quickly managers are notified of new requests"
                      options={[
                        { value: "immediate", label: "Immediate" },
                        { value: "hourly", label: "Hourly Digest" },
                        { value: "daily", label: "Daily Summary" }
                      ]}
                    />
                  </div>

                  <div className="grid grid-cols-1 gap-4">
                    <SettingsField
                      type="input"
                      name="request_expiry_hours"
                      label="Request Expiry (hours)"
                      placeholder="48"
                      inputType="number"
                      min="1"
                      max="168"
                      disabled={!isEditing}
                      form={form}
                      description="Hours before pending requests auto-expire (1-168 hours)"
                    />

                    <SwitchField
                      name="enable_employee_notifications"
                      label="Employee Notifications"
                      description="Notify employees of request status changes"
                      disabled={!isEditing}
                      form={form}
                    />
                  </div>

                  <SwitchField
                    name="enable_manager_email_notifications"
                    label="Manager Email Alerts"
                    description="Send email notifications for new requests"
                    disabled={!isEditing}
                    form={form}
                  />
                </div>

                {/* Employee Limitations Section */}
                <div className="space-y-3">
                  <SectionHeader icon={Users} title="Employee Limitations" />
                  
                  <div className="grid grid-cols-1 gap-4">
                    <SettingsField
                      type="input"
                      name="max_pending_requests"
                      label="Max Pending Requests"
                      placeholder="5"
                      inputType="number"
                      min="1"
                      max="20"
                      disabled={!isEditing}
                      form={form}
                      description="Maximum pending requests per employee"
                    />

                    <SettingsField
                      type="input"
                      name="max_requests_per_day"
                      label="Daily Request Limit"
                      placeholder="3"
                      inputType="number"
                      min="1"
                      max="10"
                      disabled={!isEditing}
                      form={form}
                      description="Maximum requests per employee per day"
                    />

                    <SwitchField
                      name="require_request_notes"
                      label="Require Notes"
                      description="Employees must provide notes with requests"
                      disabled={!isEditing}
                      form={form}
                    />
                  </div>
                </div>

                {/* Priority & Fairness Rules */}
                <div className="space-y-3">
                  <SectionHeader icon={RotateCcw} title="Priority & Fairness" />
                  
                  <SettingsField
                    type="select"
                    name="request_priority_mode"
                    label="Request Priority System"
                    placeholder="Select priority system"
                    disabled={!isEditing}
                    form={form}
                    description="How to prioritize competing requests for the same shift"
                    options={[
                      { value: "first-come", label: "First Come, First Served" },
                      { value: "seniority", label: "Seniority-Based Priority" },
                      { value: "rotation", label: "Fair Rotation System" }
                    ]}
                  />
                </div>

                {/* Advanced Features */}
                <div className="space-y-3">
                  <SectionHeader icon={Calendar} title="Advanced Features" />
                  
                  <div className="space-y-4">
                    <SwitchField
                      name="enable_shift_swapping"
                      label="Enable Shift Swapping"
                      description="Allow employees to exchange shifts with each other directly"
                      disabled={!isEditing}
                      form={form}
                    />

                    {/* Shift Swapping Sub-settings */}
                    {shiftSwappingEnabled && (
                      <div className="ml-6 space-y-4 pl-4 border-l-2 border-muted/50">
                        <div className="grid grid-cols-1 gap-4">
                          <SwitchField
                            name="require_swap_approval"
                            label="Require Manager Approval"
                            description="Manager must approve all shift swaps"
                            disabled={!isEditing}
                            form={form}
                          />

                          <SettingsField
                            type="input"
                            name="swap_advance_hours"
                            label="Swap Advance Notice (hours)"
                            placeholder="24"
                            inputType="number"
                            min="1"
                            max="168"
                            disabled={!isEditing}
                            form={form}
                            description="Minimum hours before shift to allow swaps"
                          />
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Historical Shift Protection Section - Always visible */}
            <div className="space-y-4 pt-6 border-t border-border">
              <div className="flex items-center gap-2 mb-4">
                <Lock className="h-4 w-4 text-primary" />
                <h4 className="text-sm font-semibold text-foreground">Historical Shift Protection</h4>
              </div>
              <p className="text-sm text-muted-foreground mb-4">
                Past shifts are protected from modifications to prevent back-dating. Configure a grace period during which recent shifts can still be edited freely.
              </p>

              <SettingsField
                type="input"
                name="historical_edit_grace_days"
                label="Edit Grace Period (days)"
                placeholder="3"
                inputType="number"
                min="0"
                max="30"
                disabled={!isEditing}
                form={form}
                description="Number of days after a shift's date during which it can be edited without requiring historical edit mode. Set to 0 to lock shifts immediately after their date."
              />
            </div>

            <EditControls
              isEditing={isEditing}
              loading={loading}
              onStartEdit={handleStartEditing}
              onCancel={handleCancelEditing}
              startEditText="Start Editing Shift Settings"
              saveText="Update Shift Settings"
              className="mt-6 border-t border-border"
            />
          </div>
        </form>
      </Form>
    </SettingsCard>
  );
};